/**
 * QuestionnaireEditPanel — Admin/Lab-tech editor for the customer
 * questionnaire (property_info + reason_for_testing + primary_water_use)
 * shown on the Lab Detail page. Read-only when:
 *   - test has been published (`test.status === "published"`), OR
 *   - booking already has `report_sent_at`.
 * The backend hard-locks via 409 if any report email has been sent, so
 * the UI guard is purely a UX nicety.
 *
 * Wires into:
 *   PATCH /api/admin/bookings/{reference}/property-info
 *
 * Phase 2L-A11 (Feb 2026)
 */
import { useMemo, useState } from "react";
import { ClipboardList, Edit3, Lock, Save, X, History, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PropertyIntakeForm, intakeFormToPayload, validateIntakeForm } from "@/components/PropertyIntakeForm";
import api, { formatApiError } from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { toast } from "sonner";
import { diffQuestionnaireAuditEntry, summariseQuestionnaireAuditEntry, formatQuestionnaireFieldValue } from "@/components/admin/questionnaireDiff";

// Mirror of CreateBookingModal.jsx + Booking.jsx — keep in lock-step.
const REASON_OPTIONS = [
  { value: "routine", label: "Routine water quality check" },
  { value: "drinking_safety", label: "Drinking water safety check" },
  { value: "property_sale", label: "Property sale or purchase" },
  { value: "rental", label: "Rental property / property management" },
  { value: "new_system", label: "New tank or water system check" },
  { value: "taste_odour_colour_staining", label: "Taste, odour, colour, or staining concern" },
  { value: "illness_health", label: "Illness or health-related concern" },
  { value: "other", label: "Other" },
];
const PWU_OPTIONS = [
  { value: "drinking", label: "Drinking water" },
  { value: "household", label: "Household domestic use" },
  { value: "irrigation", label: "Irrigation / garden use" },
  { value: "livestock", label: "Livestock / animals" },
  { value: "pool_spa", label: "Pool or spa top-up" },
  { value: "commercial", label: "Commercial or workplace use" },
  { value: "other", label: "Other" },
];
const REASON_LABEL = Object.fromEntries(REASON_OPTIONS.map((o) => [o.value, o.label]));
const PWU_LABEL = Object.fromEntries(PWU_OPTIONS.map((o) => [o.value, o.label]));

function formatDate(s) {
  return formatDateTimeAU(s);
}

function tankSummary(t) {
  if (!t) return "—";
  if (t.none) return "No water tanks on this property";
  const parts = [];
  if (t.poly) parts.push(`${t.poly} poly`);
  if (t.concrete) parts.push(`${t.concrete} concrete`);
  if (t.steel) parts.push(`${t.steel} steel`);
  if (t.fibreglass) parts.push(`${t.fibreglass} fibreglass`);
  return parts.length ? parts.join(", ") : "—";
}

const FILTRATION_LBL = {
  whole_house: "Whole house filtration",
  under_sink: "Under sink filtration",
  both: "Both whole-house + under sink",
  none: "No filtration",
  unsure: "Unsure",
};
const YN_LBL = { yes: "Yes", no: "No", unsure: "Unsure" };
const ISSUE_LBL = {
  bad_taste: "Bad taste", bad_smell: "Bad smell", cloudy: "Cloudy water",
  sediment: "Sediment", staining: "Staining", low_ph: "Low pH concerns",
  iron_staining: "Iron staining", filter_blocking: "Filter blocking",
  none: "No known issues",
};


export function QuestionnaireEditPanel({ booking, test, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [intakeForm, setIntakeForm] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonOther, setReasonOther] = useState("");
  const [pwu, setPwu] = useState("");
  const [pwuOther, setPwuOther] = useState("");
  // Sample identification fields — mirror the public booking form.
  const [sampleRef, setSampleRef] = useState("");
  const [samplePoint, setSamplePoint] = useState("");
  const [editReason, setEditReason] = useState("");
  const [errorMsg, setErrorMsg] = useState(null);

  const customer = booking?.customer || {};
  const propertyInfo = booking?.property_info || {};
  const sampleReferenceValue = booking?.customer_sample_reference || "";
  const samplePointValue = booking?.sample_point || "";
  const reference = booking?.reference;

  // Lock heuristic — backend is the source of truth (returns 409).
  const isLocked = useMemo(() => {
    if (!booking) return true;
    if (booking.report_sent_at) return true;
    if (test?.status === "published") return true;
    return false;
  }, [booking, test]);

  // Pull just the property_info_edited entries from the booking audit log
  // for an inline "Edit history" disclosure.
  const editHistory = useMemo(() => {
    const log = booking?.audit_log || [];
    return log
      .filter((e) => e.action === "property_info_edited")
      .slice()
      .reverse();
  }, [booking]);

  const lastEditedAt = booking?.property_info_last_edited_at;
  const lastEditedBy = booking?.property_info_last_edited_by;

  if (!booking) return null;

  const startEdit = () => {
    setIntakeForm({ ...propertyInfo });
    setReason(customer.reason_for_testing || "");
    setReasonOther(customer.reason_for_testing_other || "");
    setPwu(customer.primary_water_use || "");
    setPwuOther(customer.primary_water_use_other || "");
    setSampleRef(sampleReferenceValue);
    setSamplePoint(samplePointValue);
    setEditReason("");
    setErrorMsg(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setErrorMsg(null);
  };

  const save = async () => {
    setErrorMsg(null);
    // Client-side validation (matches the public booking form rules).
    const intakeErr = validateIntakeForm(intakeForm);
    if (intakeErr) { setErrorMsg(intakeErr); toast.error(intakeErr); return; }
    if (!reason) { setErrorMsg("Please select a Reason for testing"); toast.error("Please select a Reason for testing"); return; }
    if (reason === "other" && !reasonOther.trim()) {
      setErrorMsg("Please describe the 'Other' reason for testing");
      toast.error("Please describe the 'Other' reason for testing");
      return;
    }
    if (!pwu) { setErrorMsg("Please select Primary water use"); toast.error("Please select Primary water use"); return; }
    if (pwu === "other" && !pwuOther.trim()) {
      setErrorMsg("Please describe the 'Other' primary water use");
      toast.error("Please describe the 'Other' primary water use");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        property_info: intakeFormToPayload(intakeForm),
        reason_for_testing: reason,
        reason_for_testing_other: reason === "other" ? reasonOther.trim() : null,
        primary_water_use: pwu,
        primary_water_use_other: pwu === "other" ? pwuOther.trim() : null,
        customer_sample_reference: sampleRef.trim(),
        sample_point: samplePoint.trim(),
        edit_reason: editReason.trim() || null,
      };
      await api.patch(`/admin/bookings/${reference}/property-info`, payload);
      toast.success("Customer questionnaire updated");
      setEditing(false);
      if (onSaved) await onSaved();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Update failed";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="lab-questionnaire-panel"
      className="rounded-3xl bg-white border border-slate-200 p-6"
    >
      <header className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-cyan-600" />
            <h2 className="font-display text-lg font-semibold text-slate-900">
              Customer questionnaire
            </h2>
            {isLocked && (
              <span
                data-testid="lab-questionnaire-locked-pill"
                className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200"
              >
                <Lock className="w-3 h-3" /> Locked
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1.5">
            These answers drive recommendation logic in the Pristine summary
            (e.g. UV / coliform follow-ups). Edit before sending the report
            if the customer&apos;s answers don&apos;t reflect reality.
          </p>
          {lastEditedAt && (
            <p className="text-[11px] text-slate-500 mt-2" data-testid="lab-questionnaire-last-edited">
              Last edited by <span className="font-semibold text-slate-700">{lastEditedBy || "—"}</span>{" "}
              on <span className="font-semibold text-slate-700">{formatDate(lastEditedAt)}</span>
            </p>
          )}
        </div>
        {!editing && (
          <Button
            data-testid="lab-questionnaire-edit-btn"
            onClick={startEdit}
            disabled={isLocked}
            variant="outline"
            size="sm"
            className="rounded-full text-xs flex-shrink-0"
            title={isLocked
              ? "Questionnaire is locked because the report has been sent / published."
              : "Edit the customer's questionnaire answers"}
          >
            {isLocked ? <Lock className="w-3.5 h-3.5 mr-1" /> : <Edit3 className="w-3.5 h-3.5 mr-1" />}
            {isLocked ? "Locked" : "Edit"}
          </Button>
        )}
      </header>

      {isLocked && !editing && (
        <div
          data-testid="lab-questionnaire-locked-banner"
          className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 flex items-start gap-2"
        >
          <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            The customer report has been {test?.status === "published" ? "published" : "sent"} — questionnaire edits are no longer accepted to preserve the audit trail.
          </span>
        </div>
      )}

      {/* --- Read-only summary --- */}
      {!editing && (
        <div data-testid="lab-questionnaire-readonly" className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Row label="Reason for testing" value={
            REASON_LABEL[customer.reason_for_testing] +
            (customer.reason_for_testing === "other" && customer.reason_for_testing_other
              ? ` — “${customer.reason_for_testing_other}”` : "")
          } />
          <Row label="Primary water use" value={
            PWU_LABEL[customer.primary_water_use] +
            (customer.primary_water_use === "other" && customer.primary_water_use_other
              ? ` — “${customer.primary_water_use_other}”` : "")
          } />
          {/* Sample identification — mirrors the two optional fields on
              the public booking page. Rendered even when blank so admin
              can see they were left empty (dash placeholder). */}
          <Row
            label="Customer sample reference"
            value={sampleReferenceValue || "—"}
            data-testid="lab-questionnaire-sample-reference"
          />
          <Row
            label="Where was this sample taken from?"
            value={samplePointValue || "—"}
            data-testid="lab-questionnaire-sample-point"
          />
          <Row label="Water tanks" value={tankSummary(propertyInfo.tanks)} fullWidth />
          <Row label="Tank cleaned in last 5y" value={YN_LBL[propertyInfo.tank_cleaned_5y] || "—"} />
          <Row label="Filtration" value={FILTRATION_LBL[propertyInfo.filtration] || "—"} />
          <Row label="UV treatment" value={YN_LBL[propertyInfo.uv] || "—"} />
          <Row label="Calcite filter" value={YN_LBL[propertyInfo.calcite] || "—"} />
          <Row label="Home > 10 years old" value={YN_LBL[propertyInfo.home_older_than_10y] || "—"} />
          <Row
            label="Experienced issues"
            value={(propertyInfo.experienced_issues || []).map((k) => ISSUE_LBL[k] || k).join(", ") || "No issues reported"}
            fullWidth
          />
        </div>
      )}

      {/* --- Edit form --- */}
      {editing && (
        <div data-testid="lab-questionnaire-edit-form" className="space-y-6">
          {errorMsg && (
            <div
              data-testid="lab-questionnaire-edit-error"
              className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900 flex items-start gap-2"
            >
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Reason + PWU (top-level customer questionnaire) */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                Reason for testing
              </Label>
              <select
                data-testid="lab-questionnaire-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm focus:border-cyan-500 focus:outline-none"
              >
                <option value="">— Select —</option>
                {REASON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {reason === "other" && (
                <Input
                  data-testid="lab-questionnaire-reason-other"
                  value={reasonOther}
                  onChange={(e) => setReasonOther(e.target.value)}
                  placeholder="Please describe"
                  className="mt-2 h-11 rounded-xl"
                />
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                Primary water use
              </Label>
              <select
                data-testid="lab-questionnaire-pwu"
                value={pwu}
                onChange={(e) => setPwu(e.target.value)}
                className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm focus:border-cyan-500 focus:outline-none"
              >
                <option value="">— Select —</option>
                {PWU_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {pwu === "other" && (
                <Input
                  data-testid="lab-questionnaire-pwu-other"
                  value={pwuOther}
                  onChange={(e) => setPwuOther(e.target.value)}
                  placeholder="Please describe"
                  className="mt-2 h-11 rounded-xl"
                />
              )}
            </div>
          </div>

          {/* Sample identification — mirrors the optional block on the
              public booking page. Same wording + placeholders + limits. */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-700 mb-1">
              Sample identification <span className="text-slate-400 normal-case lowercase tracking-normal">(optional)</span>
            </div>
            <p className="text-[12px] text-slate-500 mb-3">
              These labels appear on the COA and the customer&apos;s report — leave blank if the sample doesn&apos;t need a label.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                  Customer sample reference
                </Label>
                <Input
                  data-testid="lab-questionnaire-sample-reference-input"
                  value={sampleRef}
                  onChange={(e) => setSampleRef(e.target.value)}
                  placeholder="e.g. Tank A, Unit 2, Bore near shed"
                  maxLength={80}
                  className="mt-1.5 h-11 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                  Where was this sample taken from?
                </Label>
                <Input
                  data-testid="lab-questionnaire-sample-point-input"
                  value={samplePoint}
                  onChange={(e) => setSamplePoint(e.target.value)}
                  placeholder="e.g. Kitchen tap, Tank outlet, Post-filter"
                  maxLength={120}
                  className="mt-1.5 h-11 rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Property intake (re-uses the same component as the customer-facing form) */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-700 mb-3">
              Property &amp; water system information
            </div>
            <PropertyIntakeForm
              value={intakeForm}
              onChange={setIntakeForm}
              initial={intakeForm}
              embedded
              testIdPrefix="lab-questionnaire-intake"
            />
          </div>

          {/* Edit reason (optional but recommended) */}
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">
              Reason for edit <span className="text-slate-400 font-normal lowercase">(optional, audit-logged)</span>
            </Label>
            <Textarea
              data-testid="lab-questionnaire-edit-reason"
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              placeholder="e.g. Customer confirmed they already have UV treatment installed"
              className="mt-1.5 rounded-xl"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <Button
              data-testid="lab-questionnaire-cancel-btn"
              variant="outline"
              onClick={cancel}
              disabled={saving}
              className="rounded-full"
            >
              <X className="w-4 h-4 mr-1.5" /> Cancel
            </Button>
            <Button
              data-testid="lab-questionnaire-save-btn"
              onClick={save}
              disabled={saving}
              className="rounded-full bg-slate-900 hover:bg-cyan-700 text-white font-semibold"
            >
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      )}

      {/* --- Edit history disclosure --- */}
      {!editing && editHistory.length > 0 && (
        <details
          data-testid="lab-questionnaire-history"
          className="mt-5 rounded-xl border border-slate-100 bg-slate-50/50 p-3"
        >
          <summary className="cursor-pointer text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5">
            <History className="w-3.5 h-3.5" /> Edit history ({editHistory.length})
          </summary>
          <ul className="mt-3 space-y-2.5 text-xs">
            {editHistory.map((e, i) => {
              const detailed = diffQuestionnaireAuditEntry(e);
              const oneLine = summariseQuestionnaireAuditEntry(e);
              return (
                <li key={i} className="border-l-2 border-slate-200 pl-3">
                  <div className="font-semibold text-slate-900">
                    {e.user_email || "—"} <span className="font-normal text-slate-500">· {e.user_role || "—"}</span>
                  </div>
                  <div className="text-slate-500">{formatDate(e.at)}</div>
                  <div className="text-slate-700 mt-1">{oneLine}</div>
                  {e.edit_reason && (
                    <div className="italic text-slate-600 mt-0.5">&ldquo;{e.edit_reason}&rdquo;</div>
                  )}
                  {detailed.length > 0 && (
                    <div className="mt-1 text-[11px] text-slate-600">
                      {detailed.map(({ field, before, after }) => (
                        <div key={field}>
                          <span className="font-semibold">{field}</span>:{" "}
                          <span className="line-through text-slate-400">{formatQuestionnaireFieldValue(field, before)}</span>
                          {" → "}
                          <span className="text-slate-900">{formatQuestionnaireFieldValue(field, after)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </section>
  );
}


function Row({ label, value, fullWidth, "data-testid": testId }) {
  return (
    <div className={fullWidth ? "sm:col-span-2" : ""} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5 whitespace-pre-line">{value || "—"}</div>
    </div>
  );
}
